import { decodeJwt } from "@/services/JwtService/JwtService";

/**
 * For checking expiration timestamp
 * @param expiration - expiration timestamp
 * @returns true if the token is expired, false otherwise
 * @example
 * isTokenExpired(1633072800);
 */
export const isTokenExpired = (expiration: number | undefined): boolean => {
  if (!expiration) {
    console.warn("Expiration timestamp is undefined.");
    return true;
  }

  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  return expiration < currentTimeInSeconds;
};

/**
 * For checking JWT token expiration
 * @param token - JWT token
 * @returns true if the token is expired, false otherwise
 * @example
 * isJwtTokenExpired("eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c");
 */
export const isJwtTokenExpired = (token: string | undefined): boolean => {
  try {
    if (!token) {
      return true;
    }

    const decodedJwtToken = decodeJwt(token);
    if (!decodedJwtToken?.exp) {
      console.warn("Expiration timestamp is undefined.");
      return true;
    }

    const isExpired = Date.now() >= decodedJwtToken?.exp * 1000;
    return isExpired;
  } catch (error) {
    console.error("Error checking JWT token expiration:", error);
    return true;
  }
};

/**
 * For checking seconds until token expiry
 * @param expiration - expiration timestamp in seconds (UNIX timestamp)
 * @returns seconds until token expiry (>= 0)
 * @example secondsUntilExpiry(1633072800);
 */
export const secondsUntilExpiry = (expiration: number | undefined): number => {
  try {
    if (!expiration) {
      console.warn("Expiration timestamp is undefined.");
      return 0;
    }

    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiration - currentTimeInSeconds;

    return Math.max(0, timeUntilExpiry);
  } catch (error) {
    console.error("Error calculating seconds until expiry:", error);
    return 0;
  }
};
